import { Fragment, h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";
import sortBy from "lodash/sortBy";

import i18n from "../../i18n.js";
import {
  calculateSalary,
  fetchFormData,
  selectFormDataLoaded,
  selectFormData,
  selectResult,
  selectResultLoading,
  selectResultLoadingError,
} from "../../store/salaryCalculatorStore.js";
import modulesApi from "../../api/modules";

import Alert from "../Alert.jsx";
import SubmitButton from "../SubmitButton.jsx";
import TalentFilter from "../TalentFilter.jsx";
import CurrencyField from "../CurrencyField.jsx";
import RadioButton from "../RadioButton.jsx";
import { displayMoney } from "../../helpers.js";

function createFilterItems(list, selected, sorting = true) {
  const result = list.map(([text, value]) => ({
    text,
    value,
    selected: value === selected,
  }));

  if (sorting) {
    return result.sort((a, b) => a.text.localeCompare(b.text));
  }
  return result;
}

const MONEY_MAKER_SORTING = ["me", "partner", "same"];
function sortMoneyMaker(list) {
  return sortBy(list, (item) => MONEY_MAKER_SORTING.indexOf(item.value));
}

export default function SalaryCalculatorForm({ type }) {
  const dispatch = useDispatch();

  const [countryOfResidence, setCountryOfResidence] = useState(null);
  const [workRole, setWorkRole] = useState(null);
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [maritalMoneyMaker, setMaritalMoneyMaker] = useState(null);
  const [income, setIncome] = useState("");
  const [medianHint, setMedianHint] = useState(null);

  const formData = useSelector(selectFormData);
  const formDataLoaded = useSelector(selectFormDataLoaded);
  const sending = useSelector(selectResultLoading);
  const resultsLoaded = useSelector(selectResult);
  const calculationError = useSelector(selectResultLoadingError);

  const showForm = countryOfResidence && workRole && maritalStatus;
  const currency =
    (formData.currencies && formData.currencies[countryOfResidence]) || "EUR";

  const moneyMakerOptions = sortMoneyMaker(
    createFilterItems(formData.maritalMoneyMaker || [], maritalMoneyMaker)
  );
  const disabled =
    (maritalStatus === "married" && !maritalMoneyMaker) || !income;

  type = type || "salary";

  useEffect(async () => {
    await dispatch(fetchFormData(type));
  }, []);

  useEffect(() => {
    if (!sending && resultsLoaded && !calculationError) {
      const anchor = document.getElementById("result-scroll-anchor");
      window.scrollTo(0, window.scrollY + anchor.getBoundingClientRect().top);
    }
  }, [sending, resultsLoaded, calculationError]);

  useEffect(async () => {
    if (countryOfResidence && workRole && maritalStatus) {
      const result = await modulesApi.fetchSalaryMedian(countryOfResidence);
      setMedianHint(
        i18n.t("pages.payroll_calculator.medianHint", {
          value: displayMoney(result.value, result.currency, 0),
          country: result.country,
        })
      );
    }
  }, [countryOfResidence, workRole, maritalStatus]);

  async function handleSubmit() {
    await dispatch(
      calculateSalary({
        countryOfResidence,
        currency,
        workRole,
        maritalStatus,
        maritalMoneyMaker,
        income,
        type,
      })
    );
  }

  if (!formDataLoaded) {
    return null;
  }

  return (
    <Fragment>
      {medianHint && (
        <div class="alert alert--info salary-calculator-form__median-hint">
          {medianHint}
        </div>
      )}
      <div class="screenPadding">
        <section class="nio-hero row">
          <div class="salary-calculator-form col s12">
            {calculationError && (
              <Alert class="mb-24" error>
                {i18n.t(`pages.${type}_calculator.form.error`)}
              </Alert>
            )}

            <div class="salary-calculator-form__filter-row">
              <label class="salary-calculator-form__label">
                {i18n.t(`pages.${type}_calculator.form.label`)}
              </label>
              <div class="salary-calculator-form__filters">
                <TalentFilter
                  singleSelect
                  filtered
                  name={i18n.t(
                    "pages.salary_calculator.filter.country_of_residence"
                  )}
                  items={createFilterItems(
                    formData.countries || [],
                    countryOfResidence
                  )}
                  onChange={({ value }) =>
                    setCountryOfResidence(
                      countryOfResidence === value ? null : value
                    )
                  }
                  triggerClass="mr-16 mb-12"
                />
                <TalentFilter
                  singleSelect
                  filtered
                  name={i18n.t("pages.salary_calculator.filter.job_role")}
                  items={createFilterItems(formData.workRoles || [], workRole)}
                  onChange={({ value }) =>
                    setWorkRole(workRole === value ? null : value)
                  }
                  triggerClass="mr-16 mb-12"
                />
                <TalentFilter
                  singleSelect
                  name={i18n.t("pages.salary_calculator.filter.marital_status")}
                  items={createFilterItems(
                    formData.maritalStatuses || [],
                    maritalStatus,
                    false
                  )}
                  onChange={({ value }) =>
                    setMaritalStatus(maritalStatus === value ? null : value)
                  }
                  triggerClass="mr-16 mb-12"
                />
              </div>
            </div>

            {showForm && (
              <div class="salary-calculator-form__form">
                <div class="mb-64 salary-calculator-form__input">
                  <CurrencyField
                    id="net-salary"
                    name="net-salary"
                    min="0"
                    value={income}
                    label={i18n.t(`pages.${type}_calculator.income.label`)}
                    info={i18n.t(`pages.${type}_calculator.income.info`)}
                    currency={currency}
                    onChange={setIncome}
                  />
                </div>
                {maritalStatus === "married" && (
                  <div class="mb-32 salary-calculator-form__radio-list">
                    <span class="salary-calculator-form__radio-label">
                      {i18n.t("pages.salary_calculator.filter.money_maker")}
                    </span>
                    {moneyMakerOptions.map((option) => (
                      <RadioButton
                        type="radio"
                        class="salary-calculator-form__radio-item"
                        name="marital-money-maker"
                        value={option.value}
                        checked={option.selected}
                        onChange={() => setMaritalMoneyMaker(option.value)}
                      >
                        {option.text}
                      </RadioButton>
                    ))}
                  </div>
                )}
                <SubmitButton
                  disabled={disabled}
                  sending={sending}
                  onClick={handleSubmit}
                >
                  {i18n.t(`pages.${type}_calculator.form.submit`)}
                </SubmitButton>
              </div>
            )}
          </div>
        </section>
      </div>
    </Fragment>
  );
}
